// /src/engine/exportLdraw.ts

import type { EditorSnapshot, PlacedPart, Submodel, SubmodelInstance } from '@/store/modelStore';
import { MAIN_SUBMODEL_ID } from '@/store/modelStore';
import { matrixFromArray, matrixToLdrawType1 } from './ldrawMath';

export type LDrawExportFormat = 'flat' | 'mpd';

type ExportSnapshot = Pick<EditorSnapshot, 'parts' | 'submodels'> & {
  instances?: EditorSnapshot['instances'];
};

export function exportToLDraw(
  snapshot: ExportSnapshot,
  opts?: { title?: string; includeMeta?: boolean; format?: LDrawExportFormat }
): string {
  const format = opts?.format ?? 'mpd';
  if (format === 'flat') return exportFlat(snapshot, opts);
  return exportMpd(snapshot, opts);
}

export function exportToInternalJson(snapshot: EditorSnapshot) {
  return JSON.stringify(snapshot, null, 2);
}

function exportFlat(
  snapshot: ExportSnapshot,
  opts?: { title?: string; includeMeta?: boolean }
): string {
  const title = opts?.title ?? 'Brickerator CAD Export';
  const includeMeta = opts?.includeMeta ?? true;
  const lines: string[] = [];
  lines.push(`0 ${title}`);
  lines.push(`0 Generated by Brickerator CAD (prototype)`);
  lines.push(`0`);

  const parts = snapshot.parts ?? [];
  const instances = snapshot.instances ?? [];

  const submodels: Submodel[] =
    snapshot.submodels?.length ? snapshot.submodels : [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }];

  if (includeMeta) {
    lines.push(`0 !BRICKERATOR SNAPSHOT 3`);
    for (const sm of submodels) lines.push(`0 !BRICKERATOR SUBMODELDEF ${sm.id} ${sm.name}`);
    lines.push(`0`);
 }

  const maxStep = Math.max(1, ...parts.map((p) => p.step ?? 1), ...instances.map((i) => i.step ?? 1));
  for (let step = 1; step <= maxStep; step++) {
    if (step > 1) lines.push('0 STEP');
    if (includeMeta) lines.push(`0 !BRICKERATOR STEP ${step}`);

    for (const sm of submodels) {
      const bucket = parts
        .filter((p) => (p.step ?? 1) === step && (p.submodelId ?? MAIN_SUBMODEL_ID) === sm.id)
        .sort((a, b) => a.id.localeCompare(b.id));
      const instBucket = instances
        .filter((i) => (i.step ?? 1) === step && (i.parentSubmodelId ?? MAIN_SUBMODEL_ID) === sm.id)
        .sort((a, b) => String(a.id).localeCompare(String(b.id)));
      if (bucket.length === 0 && instBucket.length === 0) continue;

      if (includeMeta) lines.push(`0 !BRICKERATOR SUBMODELBEGIN ${sm.id} ${sm.name}`);
      for (const p of bucket) {
        const m = matrixFromArray(p.matrix);
        lines.push(matrixToLdrawType1(p.ldrawColor ?? 16, p.partFile, m));
      }
      // Flat export can't create true MPD references in a single file,
      // but we can preserve intent with a meta line.
      for (const ins of instBucket) {
        lines.push(`0 !BRICKERATOR INSTANCE ${ins.id} ${ins.submodelId}`);
      }
      if (includeMeta) lines.push(`0 !BRICKERATOR SUBMODELEND ${sm.id}`);
    }
  }

  lines.push('0');
  return lines.join('\n');
}

function exportMpd(
  snapshot: ExportSnapshot,
  opts?: { title?: string; includeMeta?: boolean }
): string {
  const title = opts?.title ?? 'Brickerator CAD Export';
  const includeMeta = opts?.includeMeta ?? true;

  const parts = snapshot.parts ?? [];
  const instances = snapshot.instances ?? [];
  const submodels: Submodel[] =
    snapshot.submodels?.length ? snapshot.submodels : [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }];

  // Ensure main exists
  const hasMain = submodels.some((sm) => sm.id === MAIN_SUBMODEL_ID);
  const sms = hasMain ? submodels : [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }, ...submodels];

  const fileNameFor = (id: string) => {
    const safe = String(id ?? 'submodel')
      .trim()
      .replace(/[^a-zA-Z0-9._-]/g, '_');
    const base = safe.length ? safe : 'submodel';
    return base.toLowerCase().endsWith('.ldr') ? base : `${base}.ldr`;
  };

  const mainFile = fileNameFor(MAIN_SUBMODEL_ID);
  const subFileById = new Map<string, string>();
  for (const sm of sms) subFileById.set(sm.id, fileNameFor(sm.id));

  // Max step is global-ish for convenience; files will only emit steps they use.
  const maxStep = Math.max(
    1,
    ...parts.map((p) => p.step ?? 1),
    ...instances.map((i: any) => i.step ?? 1)
  );

  const lines: string[] = [];

  const emitFile = (smId: string) => {
    const fileName = subFileById.get(smId)!;
    const smName = sms.find((s) => s.id === smId)?.name ?? smId;

    lines.push(`0 FILE ${fileName}`);
    lines.push(`0 ${smId === MAIN_SUBMODEL_ID ? title : smName}`);
    lines.push(`0 Generated by Brickerator CAD (prototype)`);
    lines.push(`0`);

    if (includeMeta && smId === MAIN_SUBMODEL_ID) {
      lines.push(`0 !BRICKERATOR SNAPSHOT 3`);
      for (const sm of sms) lines.push(`0 !BRICKERATOR SUBMODELDEF ${sm.id} ${sm.name}`);
      lines.push(`0`);
    } else if (includeMeta) {
      lines.push(`0 !BRICKERATOR SUBMODELDEF ${smId} ${smName}`);
      lines.push(`0`);
    }

    const fileParts = parts.filter((p) => (p.submodelId ?? MAIN_SUBMODEL_ID) === smId);
    const fileInstances = instances.filter((i: any) => (i.parentSubmodelId ?? MAIN_SUBMODEL_ID) === smId);

    const stepSet = new Set<number>();
    for (const p of fileParts) stepSet.add(Math.max(1, p.step ?? 1));
    for (const i of fileInstances) stepSet.add(Math.max(1, i.step ?? 1));
    const steps = Array.from(stepSet).sort((a, b) => a - b);
    if (steps.length === 0) {
      lines.push('0 NOFILE');
      return;
    }

    let first = true;
    for (const step of steps) {
      if (!first) lines.push('0 STEP');
      first = false;
      if (includeMeta) lines.push(`0 !BRICKERATOR STEP ${step}`);

      const bucketP = fileParts
        .filter((p) => (p.step ?? 1) === step)
        .sort((a, b) => a.id.localeCompare(b.id));
      for (const p of bucketP) {
        const m = matrixFromArray(p.matrix);
        lines.push(matrixToLdrawType1(p.ldrawColor ?? 16, p.partFile, m));
      }

      const bucketI = fileInstances
        .filter((i: any) => (i.step ?? 1) === step)
        .sort((a: any, b: any) => String(a.id).localeCompare(String(b.id)));
      for (const ins of bucketI) {
        const subFile = subFileById.get(ins.submodelId) ?? fileNameFor(ins.submodelId);
        const m = matrixFromArray(ins.matrix);
        lines.push(matrixToLdrawType1(16, subFile, m));
      }
    }

    lines.push('0 NOFILE');
  };

  // Emit main first, then subfiles.
  emitFile(MAIN_SUBMODEL_ID);

  // --- SUBFILES ---
  for (const sm of sms) {
    if (sm.id === MAIN_SUBMODEL_ID) continue;
    emitFile(sm.id);
  }

  lines.push('0');
  return lines.join('\n');
}
